import { defineStore } from 'pinia'
import { ref } from 'vue'
import oidcAuth from '@/auth/oidcAuth'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

type CacheEntry = { url: string | null; pending?: Promise<string | null> }

export const useAvatarCacheStore = defineStore('avatarCache', () => {
  const cache = ref<Map<string, CacheEntry>>(new Map())

  function getCacheKey(email: string, size: string): string {
    return `${email}__${size}`
  }

  async function getPhotoUrl(email: string, size: string): Promise<string | null> {
    if (!email) return null

    const key = getCacheKey(email, size)
    const cached = cache.value.get(key)

    // Déjà résolu (succès ou échec)
    if (cached && !cached.pending) return cached.url

    // Déjà en cours de fetch
    if (cached?.pending) return cached.pending

    // Pas de token OIDC → pas d'appel Graph
    const boostCoreStore = useBoostCorePermissionStore()
    if (boostCoreStore.authMethod === 'password' || !oidcAuth.accessToken) {
      cache.value.set(key, { url: null })
      return null
    }

    // Lancer le fetch et le partager
    const pending = fetch(
      `https://graph.microsoft.com/v1.0/users/${email}/photos/${size}x${size}/$value`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${oidcAuth.accessToken}` },
      },
    )
      .then((res) => (res.ok ? res.blob() : null))
      .then((data) => {
        if (data && data.type?.includes('image')) {
          return URL.createObjectURL(data)
        }
        return null
      })
      .catch(() => null)
      .then((url) => {
        cache.value.set(key, { url })
        return url
      })

    cache.value.set(key, { url: null, pending })
    return pending
  }

  return { getPhotoUrl }
})
